/* eslint-disable @next/next/no-img-element */
import React, { useMemo } from 'react';
import classNames from 'classnames';
import CalcRadish from '$/components/CalcRadish';
import IconFont from '$/components/IconFont';
import { getAgentTeamMembershipNameByLevel } from '$/domains/teamAgent/membershipInfo';
import { useLanguageStore } from '$/store/languageStore';
import { useOfficialStore } from '$/store/useOfficialStore';
import { Popover } from 'antd';
import preQrCode from '@/assets/pre-qrcode.png';
import betaQrCode from '@/assets/beta-qrcode.png';
import relaseQrCode from '@/assets/release-qrcode.png';
import { getEnv, navigate } from '@/common/agents';
import { getHomeRouteByVersion } from '@/common/homeVersionCookie';
import { useCommonStore } from '@/store/useCommonStore';
import ProductOperation from '../ProductOperation';
import LanguageSwitch from '../LanguageSwitch';
import styles from './index.module.scss';
import UserCard from '../UserCard';
import DiscordBtn from './DiscordBtn';

const HOME_MEMBER_LEVEL_THEME = {
  // level 1 是基础会员兼容值，视觉上与 level 2 共用标准会员配色。
  1: 'homeMemberStandard',
  2: 'homeMemberStandard',
  3: 'homeMemberAdvanced',
  4: 'homeMemberElite',
  5: 'homeMemberPlatinum',
  6: 'homeMemberBlackGold',
} as const;

/** 获取首页用户胶囊中的会员名称和等级主题，会员名称只按当前 level 解析 membership_infos。 */
function getHomeMemberInfo(
  subscribe: ReturnType<typeof useOfficialStore.getState>['userInfo']['subscribe'],
  upgradeMemberText: string,
) {
  const level = Number(subscribe?.level) as keyof typeof HOME_MEMBER_LEVEL_THEME;
  if (!subscribe?.isValid) {
    return {
      text: upgradeMemberText,
      themeClassName: 'homeMemberUpgrade',
    };
  }

  return {
    text: getAgentTeamMembershipNameByLevel(subscribe.level, subscribe.membershipInfos),
    themeClassName: HOME_MEMBER_LEVEL_THEME[level] || 'homeMemberUpgrade',
  };
}

/**
 * 渲染 RoboNeo 主站固定 Header，并根据首页顶部横幅调整下移位置。
 */
export default function Header() {
  const isOversea = useOfficialStore((state) => state.isOversea());
  const languageStore = useLanguageStore((state) => state.languageStore);
  const headerConfig = useCommonStore((state) => state.headerConfig);
  const currentPathname = useCommonStore((state) => state.currentPathname);
  const path = (currentPathname || '').split('?')[0].replace(/\/$/, '') || '';
  const isHome = path === '/--roboneo/home' || path === '/home';
  const isHomeOld = path === '/--roboneo/home_old' || path === '/home_old';
  const isTeamInvite = path === '/--roboneo/team_invite' || path === '/team_invite';
  // 团队邀请页与新版首页、编辑页统一使用积分、会员等级和头像组合胶囊。
  const showUnifiedUserInfo = isHome || isTeamInvite;
  const switchToOldText = languageStore.smartcatTranslationText((v) => v.switch_to_old_version);
  const switchToNewText = languageStore.smartcatTranslationText((v) => v.switch_to_new_version);
  const upgradeMemberText = languageStore.smartcatTranslationText((v) => v.upgrade_member);
  const isUserLoggedIn = useOfficialStore((state) => state.isUserLoggedIn());
  const userSubscribe = useOfficialStore((state) => state.userInfo.subscribe);
  const openSubscribePopup = useOfficialStore((state) => state.openSubscribePopup);
  const homeMemberInfo = getHomeMemberInfo(userSubscribe, upgradeMemberText);

  /**
   * 从新版首页切换到旧版首页入口。
   */
  const goHomeOld = () => {
    window.mtstat?.track('roboneo_new_or_old_veision', {
      current_version: 'new',
    });
    navigate({ mode: 'router', pathname: getHomeRouteByVersion('old') });
  };

  /**
   * 从旧版首页切换回新版首页入口。
   */
  const goHomeNew = () => {
    window.mtstat?.track('roboneo_new_or_old_veision', {
      current_version: 'old',
    });
    navigate({ mode: 'router', pathname: getHomeRouteByVersion('new') });
  };

  const openQrCode = (open: boolean) => {
    if (open) {
      window.mtstat?.track('download_mobile_hover', {});
    }
  };

  const downloadContent = () => {
    const QR_CODE = {
      development: preQrCode,
      dev: preQrCode,
      pre: preQrCode,
      beta: betaQrCode,
      release: relaseQrCode,
    }[getEnv().ENV];

    return (
      <div className={styles.downloadPopverContent}>
        <div className={styles.qrcodeBox}>
          <img
            className={styles.qrcode}
            src={QR_CODE.src}
            alt=""
            crossOrigin="anonymous"
          />
        </div>
      </div>
    );
  };

  const wechatQrcodeContent = useMemo(() => {
    return (
      <div className={styles.wechatQrcodeContent}>
        <img src="https://titan-img.meitudata.com/roboneo/image/home/wechat_qrcode.png" alt="" crossOrigin="anonymous" />
      </div>
    );
  }, []);

  return (
    <div
      className={classNames(styles.Header, { [styles.withHomeSeedanceGuide]: isHome || isHomeOld })}
      style={{
        backgroundColor: headerConfig.showHeaderBackgroundColor ? 'var(--background-canvas)' : 'transparent',
      }}
    >

      <div className={styles.gradientBlur} style={{ visibility: headerConfig.showBlurBackground ? 'visible' : 'hidden' }}>
        <div />
        <div />
        <div />
        <div />
        <div />
        <div />
      </div>
      <div className={styles.headerContent}>
        {isHome && (
          <button className={styles.versionSwitchButton} type="button" onClick={goHomeOld} aria-label={switchToOldText}>
            <IconFont name="arrowLeftAndRightCircleBold" fontSize={16} />
            <span>{switchToOldText}</span>
          </button>
        )}
        {isHomeOld && (
          <button className={styles.versionSwitchButton} type="button" onClick={goHomeNew} aria-label={switchToNewText}>
            <IconFont name="arrowLeftAndRightCircleBold" fontSize={16} />
            <span>{switchToNewText}</span>
          </button>
        )}
        <div className={styles['product-card-box']}>
          <div className={styles['product-item']}>
            <ProductOperation />
          </div>
          {isOversea
            ? <DiscordBtn />
            : (
              <Popover
                placement="bottom"
                trigger={['hover']}
                content={wechatQrcodeContent}
                overlayClassName="resetPopoverAndArrow"
              >
                <div className={styles['product-item']}>
                  <div
                    style={{
                      width: '40px',
                      display: 'flex',
                      alignContent: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <IconFont
                      className={styles['product-item-icon']}
                      fontSize={16}
                      name="wechat"
                    />
                  </div>
                </div>
              </Popover>
            )}

          <div className={styles['product-item']}>
            <Popover
              placement="bottom"
              trigger={['hover']}
              overlayClassName="resetPopoverAndArrow"
              content={downloadContent()}
              onOpenChange={openQrCode}
            >
              <div className={styles.downloadBox} style={{ height: '100%' }}>
                <IconFont
                  className={styles['product-item-icon']}
                  fontSize={16}
                  name="mobilePhoneV2Bold"
                />
              </div>
            </Popover>
          </div>

        </div>

        {/* 语言切换 */}
        <LanguageSwitch />

        {/* 新版首页与团队邀请页将萝卜、会员等级和头像合并为与画布一致的用户胶囊。 */}
        {showUnifiedUserInfo && isUserLoggedIn ? (
          <div className={classNames(styles.homeUserInfoContainer, styles[homeMemberInfo.themeClassName])}>
            <div className={styles.homeRadishWrapper}>
              <CalcRadish
                variant="teamFlowHeader"
                hoverTriggerExtra={(
                  <>
                    <div className={styles.homeUserInfoDivider} aria-hidden="true" />
                    <button
                      type="button"
                      className={styles.homeMemberEntry}
                      onClick={() => {
                        openSubscribePopup();
                      }}
                    >
                      {homeMemberInfo.text}
                    </button>
                  </>
                )}
              />
            </div>
            <UserCard variant="teamFlowHeader" />
          </div>
        ) : (
          <>
            {/* 用户卡片 */}
            <UserCard />
            {/* 萝卜余额 */}
            <CalcRadish />
          </>
        )}
      </div>
    </div>
  );
}
